import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import {
  InventoryRequestCoordinator,
  calculateQuantityState,
  clampQuantity,
  fetchAdminProxyInventory,
  fetchCartQuantities,
  fetchStorefrontInventory,
} from '@theme/primebase-inventory-client';

const POLL_INTERVAL_MS = 60_000;
const REFRESH_THROTTLE_MS = 10_000;
const REQUEST_TIMEOUT_MS = 5_000;

class ProductInventory extends Component {
  #requests = new InventoryRequestCoordinator();
  #pollTimer;
  #lastRefreshStartedAt = 0;
  #selectionGeneration = 0;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    if (!this.#liveInventoryEnabled) return;

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    window.addEventListener('focus', this.#handleFocus);
    this.#pollTimer = window.setInterval(this.#handlePoll, POLL_INTERVAL_MS);
    this.#refreshLiveInventory({ force: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('focus', this.#handleFocus);
    if (this.#pollTimer) window.clearInterval(this.#pollTimer);
    this.#requests.abort();
  }

  get #liveInventoryEnabled() {
    if (this.dataset.liveInventoryEnabled !== 'true') return false;
    if (this.dataset.liveInventorySource === 'admin-proxy') return Boolean(this.dataset.adminInventoryProxyPath);
    return Boolean(this.dataset.storefrontPublicToken);
  }

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#refreshLiveInventory();
  };

  #handleFocus = () => this.#refreshLiveInventory();

  #handlePoll = () => {
    if (document.visibilityState === 'visible') this.#refreshLiveInventory();
  };

  /**
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const selectionGeneration = ++this.#selectionGeneration;

    try {
      const { detail } = await event.promise;
      if (selectionGeneration !== this.#selectionGeneration || !detail?.html) return;

      const { html, newProduct } = detail;
      const newInventory = html.querySelector('product-inventory');
      if (!newInventory) return;

      if (newProduct) this.dataset.productId = newProduct.id;
      for (const [name, value] of Object.entries(newInventory.dataset)) this.dataset[name] = value;

      morph(this, newInventory, { childrenOnly: true });
      if (this.#liveInventoryEnabled) this.#refreshLiveInventory({ force: true });
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[product-inventory] Product selection update failed');
    }
  };

  async #refreshLiveInventory({ force = false } = {}) {
    if (!this.#liveInventoryEnabled || document.visibilityState === 'hidden') return;

    const now = Date.now();
    if (!force && now - this.#lastRefreshStartedAt < REFRESH_THROTTLE_MS) return;
    this.#lastRefreshStartedAt = now;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const request = this.#requests.begin(variantId);
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      if (this.#requests.isCurrent(request, this.dataset.variantId)) {
        timedOut = true;
        this.#requests.abort();
      }
    }, REQUEST_TIMEOUT_MS);

    try {
      const inventoryPromise = this.#fetchInventory(variantId, request.signal);
      const cartPromise = fetchCartQuantities({
        productId: this.dataset.productId,
        variantId,
        signal: request.signal,
      }).catch(() => ({
        productCartQuantity: Number.parseInt(this.dataset.productCartQuantity || '0', 10) || 0,
        variantCartQuantity: Number.parseInt(this.dataset.variantCartQuantity || '0', 10) || 0,
      }));

      const [inventory, cartQuantities] = await Promise.all([inventoryPromise, cartPromise]);
      if (!this.#requests.isCurrent(request, this.dataset.variantId)) return;

      this.#applyLiveInventory(inventory, cartQuantities);
    } catch (error) {
      if (!this.#requests.isCurrent(request, this.dataset.variantId)) return;
      if (error?.name === 'AbortError' && !timedOut) return;
      this.#showInventoryUnavailable(error?.details?.stale === true ? 'stale' : 'error');
      console.warn(`[product-inventory] Live inventory request ${timedOut ? 'timed out' : 'failed'}`);
    } finally {
      window.clearTimeout(timeout);
    }
  }

  #fetchInventory(variantId, signal) {
    if (this.dataset.liveInventorySource === 'admin-proxy') {
      return fetchAdminProxyInventory({
        endpoint: this.dataset.adminInventoryProxyPath,
        variantId,
        signal,
      }).then((inventory) => ({
        ...inventory,
        availableForSale:
          this.dataset.variantAvailable === 'true' &&
          (this.dataset.inventoryPolicy !== 'deny' || inventory.quantityAvailable > 0),
      }));
    }

    return fetchStorefrontInventory({
      endpoint: `/api/${this.dataset.storefrontApiVersion || '2026-10'}/graphql.json`,
      publicToken: this.dataset.storefrontPublicToken,
      variantId,
      signal,
    });
  }

  #applyLiveInventory(inventory, cartQuantities) {
    const state = calculateQuantityState({
      quantityAvailable: inventory.quantityAvailable,
      availableForSale: inventory.availableForSale,
      inventoryManaged: this.dataset.inventoryManaged === 'true',
      inventoryPolicy: this.dataset.inventoryPolicy,
      purchaseLimit: this.dataset.purchaseLimit,
      quantityRuleMin: this.dataset.quantityRuleMin,
      quantityRuleMax: this.dataset.quantityRuleMax,
      quantityRuleIncrement: this.dataset.quantityRuleIncrement,
      productCartQuantity: cartQuantities.productCartQuantity,
      variantCartQuantity: cartQuantities.variantCartQuantity,
    });

    this.dataset.liveInventoryState = 'ready';
    this.dataset.liveInventorySourceActive = inventory.source || 'storefront';
    this.dataset.liveInventoryRequestId = inventory.requestId || '';
    this.dataset.liveInventoryFetchedAt = inventory.fetchedAt || '';
    this.dataset.liveInventoryUpdatedAt = inventory.inventoryUpdatedAt || '';
    this.dataset.productCartQuantity = String(cartQuantities.productCartQuantity);
    this.dataset.variantCartQuantity = String(cartQuantities.variantCartQuantity);
    this.#updateInventoryText(inventory.quantityAvailable, inventory.availableForSale);
    this.#updateQuantitySelector(state);
    this.#updatePurchaseControls(state.canPurchase);
    if (state.canPurchase) this.dataset.liquidInventoryBlocked = 'false';
  }

  #updateInventoryText(quantityAvailable, availableForSale) {
    const text = this.querySelector('.product-inventory__text');
    if (!text || this.dataset.showExactInventory !== 'true' || this.dataset.inventoryManaged !== 'true') return;

    const outOfStock = !availableForSale || (this.dataset.inventoryPolicy === 'deny' && quantityAvailable <= 0);
    text.textContent = outOfStock
      ? 'SOLD OUT'
      : quantityAvailable > 0
        ? `残り ${quantityAvailable}${this.dataset.inventoryUnit || '点'}`
        : '在庫あり';
    const icon = this.querySelector('.product-inventory__icon');
    if (icon) icon.hidden = false;
    this.#setIconStatus(outOfStock ? 'out_of_stock' : 'in_stock');
  }

  #showInventoryUnavailable(state = 'error') {
    this.dataset.liveInventoryState = state;
    const text = this.querySelector('.product-inventory__text');
    if (text && this.dataset.showExactInventory === 'true') {
      text.textContent = state === 'stale' ? '在庫確認中' : '在庫情報を更新できません';
    }
    const icon = this.querySelector('.product-inventory__icon');
    if (icon) icon.hidden = true;
  }

  #setIconStatus(status) {
    const icon = this.querySelector('.product-inventory__icon');
    if (!icon) return;
    for (const value of ['low', 'in_stock', 'out_of_stock']) icon.classList.remove(`product-inventory__icon-${value}`);
    icon.classList.add(`product-inventory__icon-${status}`);
  }

  #updateQuantitySelector(state) {
    const section = this.closest('.shopify-section, dialog');
    const selector = section?.querySelector('product-form-component quantity-selector-component');
    const input = selector?.querySelector('input[ref="quantityInput"]');
    if (!selector || !input) return;

    selector.dataset.variantId = String(this.dataset.variantId);

    input.dataset.cartQuantity = String(state.cartQuantity);

    if (state.canPurchase && (state.effectiveMaximum === null || state.effectiveMaximum >= state.minimum)) {
      const recoveringLiquidInventory = this.dataset.liquidInventoryBlocked === 'true' && input.disabled;

      if (input.dataset.liveInventoryDisabled === 'true' || recoveringLiquidInventory) {
        input.disabled = false;
        delete input.dataset.liveInventoryDisabled;
        for (const button of selector.querySelectorAll('button[data-live-inventory-disabled="true"]')) {
          button.disabled = false;
          delete button.dataset.liveInventoryDisabled;
        }
      }

      selector.updateConstraints(
        String(state.minimum),
        state.maximum === null ? null : String(state.maximum),
        String(state.increment)
      );
      input.value = String(clampQuantity(input.value, state));
      selector.updateButtonStates();
      return;
    }

    input.max = String(state.maximum ?? 0);
    input.disabled = true;
    input.dataset.liveInventoryDisabled = 'true';
    for (const button of selector.querySelectorAll('button')) {
      if (!button.disabled) button.dataset.liveInventoryDisabled = 'true';
      button.disabled = true;
    }
  }

  #updatePurchaseControls(canPurchase) {
    const section = this.closest('.shopify-section, dialog');
    const productForm = section?.querySelector('product-form-component');
    const addButton = productForm?.querySelector('[ref="addToCartButton"]');
    const acceleratedCheckout = productForm?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    const recoveringLiquidInventory = this.dataset.liquidInventoryBlocked === 'true';

    if (!canPurchase) {
      if (addButton && !addButton.disabled) {
        addButton.disabled = true;
        addButton.dataset.liveInventoryDisabled = 'true';
        this.#setButtonText(addButton, this.dataset.soldOutLabel);
      } else if (recoveringLiquidInventory) {
        this.#setButtonText(addButton, this.dataset.soldOutLabel);
      }
      if (acceleratedCheckout && !acceleratedCheckout.hidden) {
        acceleratedCheckout.hidden = true;
        acceleratedCheckout.dataset.liveInventoryDisabled = 'true';
      }
      return;
    }

    if (addButton?.dataset.liveInventoryDisabled === 'true' || (recoveringLiquidInventory && addButton?.disabled)) {
      addButton.disabled = false;
      delete addButton.dataset.liveInventoryDisabled;
      this.#setButtonText(addButton, this.dataset.addToCartLabel);
    }
    if (
      acceleratedCheckout?.dataset.liveInventoryDisabled === 'true' ||
      (recoveringLiquidInventory && acceleratedCheckout?.hidden)
    ) {
      acceleratedCheckout.hidden = false;
      delete acceleratedCheckout.dataset.liveInventoryDisabled;
    }
  }

  #setButtonText(button, label) {
    if (!button || !label) return;
    const textNode = Array.from(button.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
    );
    if (textNode) textNode.textContent = `\n    ${label}\n    `;
  }
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
